import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, Flex, HStack, VStack, Badge, Image, Divider, Spinner,
  Grid, GridItem, Link, IconButton, Tooltip, Textarea, Button, useToast,
} from '@chakra-ui/react';
import {
  FiGlobe, FiPhone, FiMail, FiMapPin, FiUser, FiBriefcase,
  FiHash, FiPercent, FiCalendar, FiExternalLink, FiEdit2, FiClipboard, FiKey } from 'react-icons/fi';
import api from '../api';
import TaskProcSafeModal from './TaskProcSafeModal';

const Field = ({ icon: Icon, label, value, mono }) => {
  if (!value) return null;
  return (
    <Box>
      <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={0.5}>{label}</Text>
      <HStack spacing={1.5}>
        {Icon && <Box color="gray.400" flexShrink={0}><Icon size={11} /></Box>}
        <Text fontSize="12px" color="gray.700" fontFamily={mono ? 'mono' : undefined} wordBreak="break-all">{value}</Text>
      </HStack>
    </Box>
  );
};

const typeColor = { customer:'blue', supplier:'green', competitor:'red', group_brand:'purple', joint_venture:'orange' };

export default function CompanyQuickViewModal({ companyId, isOpen, onClose, onEdit }) {
  const [procSafeTab, setProcSafeTab] = useState(null); // 'procedures' | 'passwords' | null
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [compRes, contRes, bankRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/contacts?company_id=${companyId}`).catch(() => ({ data: [] })),
        api.get(`/bank-accounts?company_id=${companyId}`).catch(() => ({ data: [] })),
      ]);
      setCompany(compRes.data);
      setNotesDraft(compRes.data?.notes || '');
      setContacts(Array.isArray(contRes.data) ? contRes.data.filter(c => !c.deleted_at) : []);
      setBankAccounts(Array.isArray(bankRes.data) ? bankRes.data.filter(b => !b.deleted_at) : []);
    } catch(e) {}
    setLoading(false);
  }, [companyId]);

  useEffect(() => { if (isOpen && companyId) fetchAll(); }, [isOpen, companyId, fetchAll]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/companies/${companyId}`, { notes: notesDraft });
      setCompany(c => c ? { ...c, notes: notesDraft } : c);
      toast({ title: 'Notes saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Could not save notes', status: 'error', duration: 2000 }); }
    finally { setSavingNotes(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : null;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalCloseButton />
        {onEdit && company && (
          <Tooltip label="Edit company" fontSize="xs">
            <IconButton
              icon={<FiEdit2 size={13} />} size="sm" variant="ghost"
              aria-label="Edit company"
              // Same placement as the pencil on ContactQuickViewModal:
              // sit beside the auto-rendered ModalCloseButton (right=3,
              // top=2 in Chakra defaults). right=10 leaves room for the X.
              position="absolute" top={2} right={10} zIndex={2}
              onClick={() => { onEdit(company); onClose(); }}
            />
          </Tooltip>
        )}
        {loading ? (
          <Flex justify="center" align="center" h="200px"><Spinner color="blue.400" /></Flex>
        ) : company ? (
          <>
            <ModalHeader pb={2}>
              <Flex align="center" gap={3} pr={8}>
                {company.logo_url && (
                  <Image src={`/api/files/${company.logo_url}`} alt={company.name}
                    h="40px" maxW="80px" objectFit="contain" borderRadius="md" />
                )}
                <Box flex={1}>
                  <Text fontSize="16px" fontWeight="800">{company.name}</Text>
                  <HStack spacing={1} mt={0.5} flexWrap="wrap">
                    {company.company_type && (
                      <Badge colorScheme={typeColor[company.company_type] || 'gray'} fontSize="9px">
                        {company.company_type.replace(/_/g,' ')}
                      </Badge>
                    )}
                    {company.is_client && <Badge colorScheme="teal" fontSize="9px">Client</Badge>}
                    {company.is_supplier && <Badge colorScheme="orange" fontSize="9px">Supplier</Badge>}
                  </HStack>
                </Box>
                {/* Procedures & Password Safe for this company (migration 161) */}
                <HStack spacing={1}>
                  <Tooltip label="Procedures"><IconButton size="sm" variant="ghost" colorScheme="teal"
                    icon={<FiClipboard size={16} />} aria-label="Procedures"
                    onClick={() => setProcSafeTab('procedures')} /></Tooltip>
                  <Tooltip label="Password Safe"><IconButton size="sm" variant="ghost" colorScheme="purple"
                    icon={<FiKey size={16} />} aria-label="Password Safe"
                    onClick={() => setProcSafeTab('passwords')} /></Tooltip>
                </HStack>
              </Flex>
            </ModalHeader>
            <ModalBody pt={0}>
              <VStack spacing={4} align="stretch">

                {/* Contact details */}
                <Grid templateColumns="1fr 1fr" gap={3}>
                  {company.website && (
                    <GridItem colSpan={2}>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={0.5}>Website</Text>
                        <HStack spacing={1.5}>
                          <FiGlobe size={11} color="#A0AEC0" />
                          <Link href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                            isExternal fontSize="12px" color="blue.500">
                            {company.website} <FiExternalLink size={9} style={{ display:'inline' }} />
                          </Link>
                        </HStack>
                      </Box>
                    </GridItem>
                  )}
                  <Field icon={FiPhone} label="Phone" value={company.phone} />
                  <Field icon={FiHash} label="Company Number" value={company.company_number} mono />
                  <Field icon={FiPercent} label="VAT Number" value={company.vat_number} mono />
                  <Field icon={FiBriefcase} label="Lead Source" value={company.lead_source} />
                  {company.address && (
                    <GridItem colSpan={2}>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={0.5}>Address</Text>
                        <HStack spacing={1.5} align="flex-start">
                          <Box color="gray.400" flexShrink={0} mt={0.5}><FiMapPin size={11} /></Box>
                          <Text fontSize="12px" color="gray.700" whiteSpace="pre-line">{company.address}</Text>
                        </HStack>
                      </Box>
                    </GridItem>
                  )}
                  {company.invoicing_notes && (
                    <GridItem colSpan={2}>
                      <Field label="Invoicing Notes" value={company.invoicing_notes} />
                    </GridItem>
                  )}
                  <Field icon={FiCalendar} label="Added" value={fmtDate(company.created_at)} />
                </Grid>

                {/* Relationship notes (editable) */}
                <Divider />
                <Box>
                  <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                    Notes — relationship, what they sell / buy, etc.
                  </Text>
                  <Textarea size="sm" rows={4} value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
                    placeholder="Add a summary of our relationship, what they supply or buy, key context…" />
                  <Flex justify="flex-end" mt={2}>
                    <Button size="xs" colorScheme="blue" onClick={saveNotes} isLoading={savingNotes}
                      isDisabled={notesDraft === (company.notes || '')}>Save Notes</Button>
                  </Flex>
                </Box>

                {/* Contacts */}
                {contacts.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                        Contacts ({contacts.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {contacts.map(c => (
                          <Box key={c.id} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                            <Flex align="center" gap={2}>
                              {c.avatar_url && (
                                <Image src={`/api/files/${c.avatar_url}`} w="28px" h="28px"
                                  borderRadius="full" objectFit="cover" flexShrink={0} />
                              )}
                              <Box flex={1} minW={0}>
                                <HStack spacing={2} flexWrap="wrap">
                                  <Text fontSize="12px" fontWeight="700">{c.first_name} {c.last_name || ''}</Text>
                                  {c.job_title && <Text fontSize="10px" color="gray.500">{c.job_title}</Text>}
                                </HStack>
                                <HStack spacing={3} mt={0.5} flexWrap="wrap">
                                  {c.email && (
                                    <HStack spacing={1}>
                                      <FiMail size={10} color="#A0AEC0" />
                                      <Link href={`mailto:${c.email}`} fontSize="11px" color="blue.500">{c.email}</Link>
                                    </HStack>
                                  )}
                                  {c.mobile && (
                                    <HStack spacing={1}>
                                      <FiPhone size={10} color="#A0AEC0" />
                                      <Text fontSize="11px" color="gray.600">{c.mobile}</Text>
                                    </HStack>
                                  )}
                                  {c.landline && !c.mobile && (
                                    <HStack spacing={1}>
                                      <FiPhone size={10} color="#A0AEC0" />
                                      <Text fontSize="11px" color="gray.600">{c.landline}</Text>
                                    </HStack>
                                  )}
                                </HStack>
                              </Box>
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </>
                )}

                {/* Bank accounts */}
                {bankAccounts.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                        Bank Accounts ({bankAccounts.length})
                      </Text>
                      <VStack spacing={1.5} align="stretch">
                        {bankAccounts.map(b => (
                          <Box key={b.id} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                            <HStack justify="space-between">
                              <Box>
                                <Text fontSize="12px" fontWeight="600">{b.account_name || b.bank_name}</Text>
                                {b.account_name && <Text fontSize="10px" color="gray.500">{b.bank_name}</Text>}
                              </Box>
                              <HStack spacing={1}>
                                {b.currency_code && <Badge fontSize="8px" colorScheme="blue">{b.currency_code}</Badge>}
                              </HStack>
                            </HStack>
                            <HStack fontSize="10px" color="gray.500" spacing={3} mt={0.5} flexWrap="wrap">
                              {b.sort_code && <Text>Sort: {b.sort_code}</Text>}
                              {b.account_number && <Text>Acc: {b.account_number}</Text>}
                              {b.iban && <Text>IBAN: {b.iban}</Text>}
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </>
                )}

              </VStack>
            </ModalBody>
          </>
        ) : (
          <Flex justify="center" align="center" h="120px">
            <Text fontSize="13px" color="gray.400">Company not found</Text>
          </Flex>
        )}
      </ModalContent>
    </Modal>
    <TaskProcSafeModal isOpen={!!procSafeTab} companyId={companyId}
      mode={procSafeTab}
      title={procSafeTab === 'passwords'
        ? `\u{1F510} ${company?.name || 'Company'} \u2014 Password Safe`
        : `\u{1F4CB} ${company?.name || 'Company'} \u2014 Procedures`}
      onClose={() => setProcSafeTab(null)} />
    </>
  );
}
